import type * as XLSX from "xlsx";
import type { IProjectSpreadsheetService } from "@/application/ports/services/IProjectSpreadsheetService";
import type {
  ParsedSheet,
  ParsedWorkbook,
  RawCellValue,
  RawRow,
  SpreadsheetSheet,
} from "@/domain/projectImport/types";

export class ProjectSpreadsheetService implements IProjectSpreadsheetService {
  async parse(file: File): Promise<ParsedWorkbook> {
    const XLSX = await import("xlsx");
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array", cellDates: true });

    const sheets: ParsedSheet[] = workbook.SheetNames.map((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) {
        return { sheetName, headers: [], rows: [] };
      }
      const raw = XLSX.utils.sheet_to_json<Record<string, RawCellValue>>(sheet, {
        defval: null,
        raw: false,
        dateNF: "yyyy-mm-dd",
      });
      const headers = extractHeaders(XLSX, sheet, raw);
      const rows: RawRow[] = raw.map((row) => normaliseRow(row, headers));
      return { sheetName, headers, rows };
    }).filter((s) => s.headers.length > 0);

    return { sheets };
  }

  async download(sheets: readonly SpreadsheetSheet[], filename: string): Promise<void> {
    const XLSX = await import("xlsx");
    const workbook = XLSX.utils.book_new();
    for (const spec of sheets) {
      const sheet = XLSX.utils.aoa_to_sheet(spec.rows);
      if (spec.columnWidths) sheet["!cols"] = spec.columnWidths.map((wch) => ({ wch }));
      XLSX.utils.book_append_sheet(workbook, sheet, spec.name);
    }
    XLSX.writeFile(workbook, filename);
  }
}

function extractHeaders(
  XLSX: typeof import("xlsx"),
  sheet: XLSX.WorkSheet,
  parsedRows: Record<string, RawCellValue>[],
): string[] {
  // Try the SheetJS header row first (range A1:_).
  const range = sheet["!ref"];
  if (range) {
    const headersFromRange = XLSX.utils
      .sheet_to_json<unknown[]>(sheet, { header: 1, raw: false })
      .shift() as unknown[] | undefined;
    if (headersFromRange) {
      return headersFromRange
        .map((h) => (h == null ? "" : String(h).trim()))
        .filter((h) => h !== "");
    }
  }
  // Fallback: union of keys from parsed rows.
  const seen = new Set<string>();
  for (const row of parsedRows) {
    for (const k of Object.keys(row)) seen.add(k);
  }
  return [...seen];
}

function normaliseRow(row: Record<string, RawCellValue>, headers: string[]): RawRow {
  const out: RawRow = {};
  for (const h of headers) {
    out[h] = row[h] ?? null;
  }
  return out;
}
